import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { isManagerUser } from './authRoles';
import './CheckInScanPage.css';
import './ChecklistShiftPages.css';
import logo from './assets/newlogo.png';
import dukkan from './assets/dukkan2.jpg';

const DAY_LABELS = {
  Pzt: 'Pazartesi',
  Sal: 'Salı',
  Car: 'Çarşamba',
  Per: 'Perşembe',
  Cum: 'Cuma',
  Cmt: 'Cumartesi',
  Paz: 'Pazar',
};

function formatWeek(isoMonday) {
  if (!isoMonday) return '—';
  const mon = new Date(`${isoMonday}T12:00:00`);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  return `${mon.toLocaleDateString('tr-TR')} – ${sun.toLocaleDateString('tr-TR')}`;
}

export default function BaristaHomePage() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [name, setName] = useState('');
  const [portalTitle, setPortalTitle] = useState('Barista');
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      const u = data?.user;
      setUserId(u?.id ?? null);
      setName(u?.user_metadata?.full_name || u?.email || '');
      setPortalTitle(u && isManagerUser(u) ? 'Vardiya müdürü' : 'Barista');
    });
  }, []);

  const loadShifts = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setErr('');
    const { data, error } = await supabase
      .from('shifts')
      .select('*')
      .contains('assigned_user_ids', [userId])
      .order('week_start', { ascending: false })
      .order('sort_order', { ascending: true });
    if (error) setErr(error.message);
    else setShifts(data || []);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    loadShifts();
  }, [loadShifts]);

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="checkin-page">
      <div className="checkin-content">
        <div className="checkin-left">
          <img src={logo} alt="" className="checkin-logo" style={{ height: 72 }} />
          <h1 className="checkin-title">{portalTitle}</h1>
          <p className="checkin-sub">Merhaba{name ? `, ${name}` : ''}.</p>

          <section style={{ marginBottom: '1.5rem' }}>
            <h2 className="checkin-card-title">Günlük checklist</h2>
            <p className="checkin-card-sub">
              Mağazanızın günlük görev listesini işaretleyin ve gün notlarını girin.
            </p>
            <Link to="/barista/checklist" className="checkin-btn" style={{ display: 'inline-block', marginTop: 8, textDecoration: 'none', textAlign: 'center' }}>
              Checklist&apos;i aç
            </Link>
          </section>

          <section style={{ marginBottom: '1.5rem' }}>
            <h2 className="checkin-card-title">Giriş (check-in)</h2>
            <p className="checkin-card-sub">
              İşe başlarken mağazadaki ortak QR kodu okutun; açılan sayfada barista hesabınızla giriş yapın — kayıt otomatik oluşur.
            </p>
            <p className="checkin-card-sub">
              <Link to="/check-in">Check-in sayfası</Link> (doğrudan link ile de açılabilir).
            </p>
          </section>

          <section>
            <h2 className="checkin-card-title">Vardiyalarınız</h2>
            <p className="checkin-card-sub">Size atanan vardiyalar aşağıdadır.</p>
            {err ? <p className="checkin-scanned-warn">{err}</p> : null}
            {loading ? (
              <p className="checkin-card-sub">Yükleniyor…</p>
            ) : shifts.length === 0 ? (
              <p className="checkin-card-sub">Henüz size atanmış vardiya yok. Vardiya müdürünüz veya işletme sahibi sizi vardiyaya eklemelidir.</p>
            ) : (
              <div className="shift-table-wrap" style={{ marginTop: 8 }}>
                <table className="shift-table">
                  <thead>
                    <tr>
                      <th>Hafta</th>
                      <th>Gün</th>
                      <th>Saat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shifts.map((s) => (
                      <tr key={s.id}>
                        <td>{formatWeek(s.week_start)}</td>
                        <td><span className="shift-day-badge">{s.day}</span> {DAY_LABELS[s.day] || ''}</td>
                        <td>{s.start_time} – {s.end_time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <button type="button" className="checkin-btn" style={{ marginTop: 16 }} onClick={loadShifts}>
              Listeyi yenile
            </button>
          </section>

          <button
            type="button"
            onClick={signOut}
            style={{
              marginTop: 24,
              background: 'transparent',
              border: '1px solid #e2e8f0',
              borderRadius: 12,
              padding: '0.75rem 1rem',
              cursor: 'pointer',
              width: '100%',
              color: '#64748b',
            }}
          >
            Çıkış yap
          </button>
        </div>
        <div className="checkin-right" aria-hidden>
          <img src={dukkan} alt="" />
        </div>
      </div>
    </div>
  );
}
